import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, ChevronDown, ExternalLink, Loader2, Minus, RefreshCw } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { EmptyState, RelativeTime, SectionCard } from '../../../components/design-system/admin';
import { BarList, ScoreRing, TrendChart } from '../../../components/design-system/analytics';
import {
    VisibilityHistoryPoint,
    VisibilityLatest,
    useAiChannelsApi,
} from '../../../common/hooks/use-ai-channels-api.hook';
import { readApiError } from '../../../common/utils/api-error';
import { competitorTally, formatUsd, rowsByPrompt, runProgress, scoreBand, scoreTrend, sourceTally, visibilityCounts } from './ai-channels';

const MAX_CHIPS = 3;

/**
 * Does an AI assistant name the shop when a shopper asks where to buy what it
 * sells? The score, its trend, every question with what each engine answered,
 * who it named instead, and — in plain words — what moves the score.
 */
export function VisibilityTab({ onOpenAssistants }: { onOpenAssistants: () => void }) {
    const { t } = useTranslation();
    const api = useAiChannelsApi();
    const [latest, setLatest] = useState<VisibilityLatest | null>(null);
    const [history, setHistory] = useState<VisibilityHistoryPoint[]>([]);
    const [starting, setStarting] = useState(false);
    const [open, setOpen] = useState<string | null>(null);
    const [weekly, setWeekly] = useState<boolean | null>(null);

    const load = useCallback(async () => {
        const [l, h] = await Promise.all([api.latestVisibility(), api.visibilityHistory().catch(() => [])]);
        setLatest(l);
        setHistory(h);
        setWeekly((w) => (w === null ? l.weekly : w));
    }, [api]);

    useEffect(() => {
        load().catch(() => setLatest({ check: null, lastDone: null, plan: { prompts: [], engines: [], budgetUsd: 0.1 }, weekly: true }));
    }, [load]);

    const running = latest?.check?.status === 'running';
    // While a check runs, read it again every few seconds: answers arrive one question at a time.
    useEffect(() => {
        if (!running) return;
        const id = setInterval(() => load().catch(() => undefined), 4000);
        return () => clearInterval(id);
    }, [running, load]);

    const shown = latest?.check?.status === 'done' ? latest.check : latest?.lastDone ?? (latest?.check?.status === 'running' ? latest.check : null);
    const rows = useMemo(() => rowsByPrompt(shown ?? null), [shown]);
    const counts = visibilityCounts(shown?.results);
    const competitors = competitorTally(shown?.results);
    const sources = sourceTally(shown?.results);
    const trend = scoreTrend(history);
    const progress = runProgress(running ? latest?.check ?? null : null);
    const band = scoreBand(shown?.status === 'done' ? shown.score : null);

    const run = async () => {
        setStarting(true);
        try {
            await api.runVisibility();
            toast.success(t('AI_CHANNELS.VISIBILITY.STARTED'));
            await load();
        } catch (err) {
            const body = await readApiError(err);
            toast.error(body.message ? String(body.message) : t('AI_CHANNELS.VISIBILITY.RUN_FAILED'));
        } finally {
            setStarting(false);
        }
    };

    const toggleWeekly = async (value: boolean) => {
        setWeekly(value);
        try {
            await api.updateSettings({ visibility: { weekly: value } });
        } catch {
            setWeekly(!value);
            toast.error(t('AI_CHANNELS.SAVE_FAILED'));
        }
    };

    if (!latest) return <Skeleton className="h-64 rounded-2xl" />;

    return (
        <div className="flex flex-col gap-6" data-testid="ai-visibility">
            {/* The score, and the button */}
            <SectionCard
                title={t('AI_CHANNELS.VISIBILITY.SCORE_TITLE')}
                description={t('AI_CHANNELS.VISIBILITY.SCORE_DESC')}
                action={
                    <Button onClick={run} disabled={starting || running} data-testid="ai-visibility-run">
                        {starting || running ? <Loader2 className="animate-spin" /> : <RefreshCw />}
                        {running ? t('AI_CHANNELS.VISIBILITY.RUNNING', { done: progress.done, total: progress.total }) : t('AI_CHANNELS.VISIBILITY.RUN_NOW')}
                    </Button>
                }
            >
                {shown && shown.status === 'done' ? (
                    <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
                        <ScoreRing
                            value={shown.score ?? 0}
                            size={104}
                            label={t('AI_CHANNELS.VISIBILITY.SCORE_LABEL')}
                            sublabel={band ? t(`AI_CHANNELS.VISIBILITY.BAND.${band.toUpperCase()}`) : undefined}
                            testId="ai-visibility-score"
                        />
                        <dl className="grid flex-1 grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-4 md:max-w-xl">
                            <div>
                                <dt className="text-xs text-muted-foreground">{t('AI_CHANNELS.VISIBILITY.NAMED')}</dt>
                                <dd className="tabular-nums">{t('AI_CHANNELS.VISIBILITY.OF', { count: counts.named, total: counts.answers })}</dd>
                            </div>
                            <div>
                                <dt className="text-xs text-muted-foreground">{t('AI_CHANNELS.VISIBILITY.CITED')}</dt>
                                <dd className="tabular-nums">{t('AI_CHANNELS.VISIBILITY.OF', { count: counts.cited, total: counts.answers })}</dd>
                            </div>
                            <div>
                                <dt className="text-xs text-muted-foreground">{t('AI_CHANNELS.VISIBILITY.CHECKED')}</dt>
                                <dd><RelativeTime value={shown.finishedAt ?? shown.createdAt} /></dd>
                            </div>
                            <div>
                                <dt className="text-xs text-muted-foreground">{t('AI_CHANNELS.VISIBILITY.COST')}</dt>
                                <dd className="tabular-nums">{formatUsd(shown.costUsd)}</dd>
                            </div>
                        </dl>
                    </div>
                ) : running ? (
                    <p className="text-sm text-muted-foreground">{t('AI_CHANNELS.VISIBILITY.RUNNING_HINT')}</p>
                ) : (
                    <EmptyState
                        title={t('AI_CHANNELS.VISIBILITY.EMPTY_TITLE')}
                        description={t('AI_CHANNELS.VISIBILITY.EMPTY_DESC', { count: latest.plan.prompts.length, engines: latest.plan.engines.map((e) => e.label).join(', ') })}
                    />
                )}
                <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t pt-4 text-xs text-muted-foreground">
                    <span>
                        {t('AI_CHANNELS.VISIBILITY.HOW', {
                            count: latest.plan.prompts.length,
                            engines: latest.plan.engines.map((e) => e.label).join(' + '),
                            budget: formatUsd(latest.plan.budgetUsd),
                        })}
                    </span>
                    <label className="flex items-center gap-2">
                        <Switch checked={!!weekly} onCheckedChange={toggleWeekly} aria-label={t('AI_CHANNELS.VISIBILITY.WEEKLY') as string} />
                        {t('AI_CHANNELS.VISIBILITY.WEEKLY')}
                    </label>
                </div>
            </SectionCard>

            {/* The trend */}
            <SectionCard title={t('AI_CHANNELS.VISIBILITY.TREND_TITLE')} description={t('AI_CHANNELS.VISIBILITY.TREND_DESC')}>
                {trend.length >= 2 ? (
                    <TrendChart data={trend} valueLabel={t('AI_CHANNELS.VISIBILITY.SCORE_LABEL')} height={180} formatValue={(v) => String(Math.round(v))} testId="ai-visibility-trend" />
                ) : (
                    <p className="text-sm text-muted-foreground">{t('AI_CHANNELS.VISIBILITY.TREND_SOON')}</p>
                )}
            </SectionCard>

            {/* Every question */}
            <SectionCard title={t('AI_CHANNELS.VISIBILITY.TABLE_TITLE')} description={t('AI_CHANNELS.VISIBILITY.TABLE_DESC')} flush>
                {rows.length ? (
                    <Table data-testid="ai-visibility-table">
                        <TableHeader>
                            <TableRow>
                                <TableHead className="ps-6">{t('AI_CHANNELS.VISIBILITY.COL_PROMPT')}</TableHead>
                                <TableHead>{t('AI_CHANNELS.VISIBILITY.COL_ENGINE')}</TableHead>
                                <TableHead className="text-center">{t('AI_CHANNELS.VISIBILITY.COL_NAMED')}</TableHead>
                                <TableHead className="text-center">{t('AI_CHANNELS.VISIBILITY.COL_POSITION')}</TableHead>
                                <TableHead>{t('AI_CHANNELS.VISIBILITY.COL_COMPETITORS')}</TableHead>
                                <TableHead className="pe-6">{t('AI_CHANNELS.VISIBILITY.COL_SOURCES')}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {rows.map((r) => {
                                const key = `${r.promptId}:${r.engine}`;
                                const expanded = open === key;
                                return (
                                    <Fragment key={key}>
                                        <TableRow className="cursor-pointer align-top" onClick={() => setOpen(expanded ? null : key)} aria-expanded={expanded}>
                                            <TableCell className="max-w-[280px] ps-6 whitespace-normal">
                                                <span className="flex items-start gap-1.5">
                                                    <ChevronDown className={cn('mt-0.5 size-3.5 shrink-0 text-muted-foreground transition-transform', expanded && 'rotate-180')} aria-hidden />
                                                    <span>{r.prompt}</span>
                                                </span>
                                            </TableCell>
                                            <TableCell className="whitespace-nowrap text-muted-foreground">{r.engineLabel}</TableCell>
                                            <TableCell className="text-center">
                                                {r.error ? (
                                                    <span className="text-xs text-muted-foreground">{t('AI_CHANNELS.VISIBILITY.NO_ANSWER')}</span>
                                                ) : r.mentioned ? (
                                                    <Check className="mx-auto size-4 text-success" aria-label={t('AI_CHANNELS.VISIBILITY.YES') as string} />
                                                ) : (
                                                    <Minus className="mx-auto size-4 text-muted-foreground" aria-label={t('AI_CHANNELS.VISIBILITY.NO') as string} />
                                                )}
                                            </TableCell>
                                            <TableCell className="text-center tabular-nums">{r.position ?? '–'}</TableCell>
                                            <TableCell className="max-w-[260px] whitespace-normal">
                                                <span className="flex flex-wrap gap-1">
                                                    {r.competitors.slice(0, MAX_CHIPS).map((c) => (
                                                        <Badge key={c} variant="outline" className="font-normal">{c}</Badge>
                                                    ))}
                                                    {r.competitors.length > MAX_CHIPS && (
                                                        <span className="text-xs text-muted-foreground">+{r.competitors.length - MAX_CHIPS}</span>
                                                    )}
                                                </span>
                                            </TableCell>
                                            <TableCell className="max-w-[200px] pe-6 whitespace-normal text-xs text-muted-foreground">
                                                {r.sources.slice(0, 3).map((s) => s.domain).filter((d, i, all) => all.indexOf(d) === i).join(', ') || '–'}
                                                {r.sources.length > 3 && ` +${r.sources.length - 3}`}
                                            </TableCell>
                                        </TableRow>
                                        {expanded && (
                                            <TableRow className="bg-muted/30 hover:bg-muted/30">
                                                <TableCell colSpan={6} className="px-6 py-4 whitespace-normal">
                                                    <div className="flex flex-col gap-3 text-sm">
                                                        {r.excerpt && <p className="leading-relaxed">“{r.excerpt}”</p>}
                                                        {r.competitors.length > 0 && (
                                                            <p className="text-xs text-muted-foreground">
                                                                {t('AI_CHANNELS.VISIBILITY.ALL_NAMED')}: {r.competitors.join(', ')}
                                                            </p>
                                                        )}
                                                        {r.sources.length > 0 && (
                                                            <ul className="flex flex-col gap-1 text-xs">
                                                                {r.sources.map((s) => (
                                                                    <li key={s.url}>
                                                                        <a href={s.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground" onClick={(e) => e.stopPropagation()}>
                                                                            <ExternalLink className="size-3" aria-hidden />
                                                                            {s.title || s.domain}
                                                                        </a>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        )}
                                                        <p className="text-[11px] text-muted-foreground">{r.model}{typeof r.costUsd === 'number' ? ` · ${formatUsd(r.costUsd)}` : ''}</p>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                ) : (
                    <div className="px-6 pb-6">
                        <ol className="flex list-decimal flex-col gap-1.5 ps-5 text-sm text-muted-foreground">
                            {latest.plan.prompts.map((p) => <li key={p.id}>{p.text}</li>)}
                        </ol>
                    </div>
                )}
                {shown && shown.skipped > 0 && (
                    <p className="px-6 pb-5 pt-3 text-xs text-muted-foreground">{t('AI_CHANNELS.VISIBILITY.SKIPPED', { count: shown.skipped, budget: formatUsd(shown.budgetUsd ?? latest.plan.budgetUsd) })}</p>
                )}
            </SectionCard>

            <div className="grid gap-6 md:grid-cols-2">
                <SectionCard title={t('AI_CHANNELS.VISIBILITY.INSTEAD_TITLE')} description={t('AI_CHANNELS.VISIBILITY.INSTEAD_DESC')}>
                    <BarList
                        items={competitors.map((c) => ({ key: c.name, label: c.name, value: c.count }))}
                        format={(v) => t('AI_CHANNELS.VISIBILITY.ANSWERS', { count: v })}
                        emptyText={t('AI_CHANNELS.VISIBILITY.INSTEAD_EMPTY') as string}
                        testId="ai-visibility-competitors"
                    />
                    {sources.length > 0 && (
                        <p className="mt-4 text-xs text-muted-foreground">
                            {t('AI_CHANNELS.VISIBILITY.READ_FROM')}: {sources.slice(0, 6).map((s) => s.domain).join(', ')}
                        </p>
                    )}
                </SectionCard>

                <SectionCard title={t('AI_CHANNELS.VISIBILITY.MOVES_TITLE')} description={t('AI_CHANNELS.VISIBILITY.MOVES_DESC')}>
                    <ul className="flex flex-col gap-3 text-sm">
                        {['LISTINGS', 'PAGES', 'PRICES', 'PRESS', 'FEED'].map((k) => (
                            <li key={k} className="flex flex-col gap-0.5">
                                <span>{t(`AI_CHANNELS.VISIBILITY.MOVES.${k}`)}</span>
                                <span className="text-xs leading-relaxed text-muted-foreground">{t(`AI_CHANNELS.VISIBILITY.MOVES.${k}_WHY`)}</span>
                            </li>
                        ))}
                    </ul>
                    <Button variant="outline" size="sm" className="mt-4" onClick={onOpenAssistants}>
                        {t('AI_CHANNELS.VISIBILITY.OPEN_ASSISTANTS')}
                    </Button>
                </SectionCard>
            </div>
        </div>
    );
}
